'use client';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { ResourceResult } from '@/components/ResourceCenter/ResourceResult';
import { Search } from '@/components/Search';
import { ResourceResultType, useGetResourcesSearchQuery } from '@/lib/queries/Resources';
import { ResourceCategory } from '@/types';

const MAX_RESOURCES = 18;

interface ResourceSearchProps {
  title?: string | ReactNode;
  categories?: string[];
  draftMode?: boolean;
  showResultsBeforeQuery?: boolean;
  maxResults?: number;
}

export function ResourceSearch({
  title = 'Search',
  categories = [],
  draftMode = false,
  showResultsBeforeQuery = true,
  maxResults = MAX_RESOURCES,
}: ResourceSearchProps) {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const hasTriggeredRefetch = useRef(false);

  const handleSearch = (term: string = '') => {
    setSearchTerm(term);
    setCurrentPage(0);
  };

  // Determine if we should show results before query
  // If showResultsBeforeQuery is true, we always execute the query (even with empty search term)
  // If showResultsBeforeQuery is false, only execute when there's a search term
  const shouldExecuteQuery = showResultsBeforeQuery || searchTerm.trim().length > 0;

  // Use empty string when showResultsBeforeQuery is true and no search term (to get all resources)
  const effectiveSearchTerm =
    showResultsBeforeQuery && searchTerm.trim().length === 0 ? '' : searchTerm;

  const {
    data: searchData,
    error: searchError,
    loading: searchLoading,
    refetch,
  } = useGetResourcesSearchQuery(
    effectiveSearchTerm,
    categories,
    currentPage,
    maxResults,
    draftMode,
    !shouldExecuteQuery // Skip query if we shouldn't execute it
  );

  // Ensure query executes on mount for statically generated pages
  // This fixes the issue where search results don't load until first user interaction
  useEffect(() => {
    // Only trigger once on mount when we should execute the query
    // This ensures the query runs even if Apollo Client doesn't execute it automatically
    if (shouldExecuteQuery && !hasTriggeredRefetch.current) {
      hasTriggeredRefetch.current = true;
      // Use setTimeout to ensure this runs after the component is fully mounted
      // and Apollo Client is ready
      const timeoutId = setTimeout(() => {
        refetch();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps array - only run once on mount

  useEffect(() => {
    if (searchError) {
      console.error('Error fetching resources:', searchError);
    }
  }, [searchError]);

  return (
    <div data-component="ResourceSearch">
      <Search<ResourceResultType>
        title={title}
        defaultValue={searchTerm}
        onSearch={(term) => {
          handleSearch(term);
        }}
        setPage={setCurrentPage}
        isSearching={searchLoading}
        results={searchData?.resourceArticles ?? []}
        showResultsBeforeQuery={showResultsBeforeQuery}
        renderResult={(result: ResourceResultType) => (
          <ResourceResult
            key={result.Slug}
            title={result.ResourceTitle}
            description={result.ResourceDescription ?? undefined}
            thumbnail={result?.ResourceThumbnail ?? undefined}
            slug={result.Slug}
            resourceCategory={result.ResourceCategory as ResourceCategory | undefined}
          />
        )}
        indexProp="Slug"
      />
    </div>
  );
}
